import {
  Component,
  inject,
  signal,
  OnInit,
  ChangeDetectionStrategy,
  CUSTOM_ELEMENTS_SCHEMA,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, Validators, ReactiveFormsModule } from '@angular/forms';
import {
  DialogRef,
  InputComponent,
  FieldLabelComponent,
  GridFormComponent,
  GridFormItemComponent,
  IconComponent,
  type IconName,
  NebulaToastService,
} from 'nebula-ui-kit';
import {
  PerfilService,
  PerfilData,
  TranslationService,
  TranslatePipe,
  UiProfileService,
} from '@nebula-shared/data-access';

/**
 * Modal de PERFIL propio (autoservicio, patrón estándar de la app: `lib-dialog` + cabecera). Se abre
 * desde la opción "Perfil" del menú del header. Solo permite editar datos personales: nombres,
 * apellidos y correo. Usuario, tipo y número de documento se muestran de solo lectura.
 */
@Component({
  selector: 'app-perfil-modal',
  standalone: true,
  templateUrl: './perfil-modal.component.html',
  styleUrls: ['./perfil-modal.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputComponent,
    FieldLabelComponent,
    GridFormComponent,
    GridFormItemComponent,
    IconComponent,
    TranslatePipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PerfilModalComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly perfilService = inject(PerfilService);
  private readonly translation = inject(TranslationService);
  private readonly dialogRef = inject<DialogRef<PerfilModalComponent, PerfilData | null>>(
    DialogRef,
    {
      optional: true,
    },
  );

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly errorMsg = signal('');

  /** Perfil cargado; se reenvía completo al guardar (el backend solo aplica los 3 campos editables). */
  private loaded: PerfilData | null = null;

  readonly form = this.fb.group({
    username: [{ value: '', disabled: true }],
    tipoDocumento: [{ value: '', disabled: true }],
    numeroDocumento: [{ value: '', disabled: true }],
    nombres: ['', [Validators.required, Validators.maxLength(100)]],
    apellidos: ['', [Validators.required, Validators.maxLength(100)]],
    email: ['', [Validators.required, Validators.email, Validators.maxLength(120)]],
  });

  ngOnInit(): void {
    this.cargar();
  }

  /** Pide el perfil; con el 3.0 un fallo deja la pantalla en error con reintentar. */
  cargar(): void {
    this.loading.set(true);
    this.cargaFallida.set(false);
    this.perfilService.getMiPerfil().subscribe({
      next: (perfil) => {
        this.patch(perfil);
        this.loading.set(false);
      },
      error: () => {
        this.errorMsg.set(this.translation.translate('PROFILE.ERROR.LOAD'));
        this.cargaFallida.set(true);
        this.loading.set(false);
      },
    });
  }

  private patch(perfil: PerfilData): void {
    this.loaded = perfil;
    const codigo = perfil.tipoDocumentoCodigo ?? '';
    this.form.patchValue({
      username: perfil.username ?? '',
      tipoDocumento: this.getTipoDocumentoLabel(codigo),
      numeroDocumento: perfil.numeroDocumento ?? '',
      nombres: perfil.nombres ?? '',
      apellidos: perfil.apellidos ?? '',
      email: perfil.email ?? '',
    });
  }

  private getTipoDocumentoLabel(codigo: string): string {
    const keyMap: Record<string, string> = {
      CC: 'PROFILE.TYPES.CC',
      RC: 'PROFILE.TYPES.RC',
      TI: 'PROFILE.TYPES.TI',
      PP: 'PROFILE.TYPES.PP',
      NIT: 'PROFILE.TYPES.NIT',
      CE: 'PROFILE.TYPES.CE',
      PEP: 'PROFILE.TYPES.PEP',
      IE: 'PROFILE.TYPES.IE',
    };

    const translationKey = keyMap[codigo];
    return translationKey ? this.translation.translate(translationKey) : codigo;
  }

  getError(key: string): string {
    const control = this.form.get(key);
    if (!control || !control.touched) return '';

    if (control.hasError('required')) {
      return this.translation.translate('PROFILE.FIELD.REQUIRED');
    }
    if (control.hasError('email')) {
      return this.translation.translate('PROFILE.FIELD.INVALID_EMAIL');
    }
    if (control.hasError('maxlength')) {
      const maxLength = control.errors?.['maxlength']?.requiredLength;
      return this.translation.translate('PROFILE.FIELD.MAX_LENGTH').replace('{length}', maxLength);
    }
    return '';
  }

  guardar(): void {
    if (this.form.invalid || !this.loaded) {
      this.form.markAllAsTouched();
      return;
    }
    this.saving.set(true);
    this.errorMsg.set('');
    const dto: PerfilData = {
      ...this.loaded,
      nombres: this.form.value.nombres ?? '',
      apellidos: this.form.value.apellidos ?? '',
      email: this.form.value.email ?? '',
    };
    // El correo se toma del token: uno nuevo surte efecto en el siguiente inicio de sesion
    // (decision de Carlos, 13-oct-2026).
    const cambioCorreo = (dto.email ?? '').trim() !== (this.loaded.email ?? '').trim();
    this.perfilService.actualizarMiPerfil(dto).subscribe({
      next: (perfil) => {
        this.saving.set(false);
        if (cambioCorreo) {
          this.avisos.info(this.translation.translate('PROFILE.NOTICE.EMAIL_NEXT_LOGIN'));
        }
        this.dialogRef?.close(perfil ?? dto);
      },
      error: () => {
        this.saving.set(false);
        // Con el 3.0 el fallo de guardar es resultado de una accion: va en toast, no dentro
        // del formulario. El perfil vigente conserva su aviso.
        if (this.esPerfil30()) {
          this.avisos.error(this.translation.translate('PROFILE.ERROR.SAVE'));
        } else {
          this.errorMsg.set(this.translation.translate('PROFILE.ERROR.SAVE'));
        }
      },
    });
  }

  cerrar(): void {
    this.dialogRef?.close(null);
  }

  // ========================================
  // PERFIL 3.0
  // ========================================
  private readonly uiProfile = inject(UiProfileService);
  private readonly avisos = inject(NebulaToastService);

  /** La carga fallo y no hay datos que editar; solo lo pinta el perfil 3.0. */
  readonly cargaFallida = signal(false);

  /** Si el modal se pinta con el diseño del perfil 3.0; el perfil vigente conserva el suyo. */
  esPerfil30(): boolean {
    return this.uiProfile.esPerfil30();
  }

  /** Codigo del tipo de documento (CC, NIT...) tal como llega del perfil. */
  tipoDocumentoCodigo(): string {
    return this.loaded?.tipoDocumentoCodigo ?? '';
  }

  /** Nombre como se va a guardar: sigue lo que se escribe en el formulario. */
  nombreCompleto(): string {
    const { nombres, apellidos } = this.form.getRawValue();
    return `${(nombres ?? '').trim()} ${(apellidos ?? '').trim()}`.trim() || '—';
  }

  /** Iniciales del avatar: primera letra de los nombres y de los apellidos. */
  iniciales(): string {
    const { nombres, apellidos } = this.form.getRawValue();
    const letras = ((nombres ?? '').trim()[0] ?? '') + ((apellidos ?? '').trim()[0] ?? '');
    return letras.toUpperCase() || '—';
  }

  /** Si algo de lo editable difiere de lo que se cargo. */
  private hayCambios(): boolean {
    if (!this.loaded) return false;
    const v = this.form.getRawValue();
    return (
      (v.nombres ?? '').trim() !== (this.loaded.nombres ?? '') ||
      (v.apellidos ?? '').trim() !== (this.loaded.apellidos ?? '') ||
      (v.email ?? '').trim() !== (this.loaded.email ?? '')
    );
  }

  /** Guardar solo se ofrece con cambios validos y sin otro guardado en curso. */
  puedeGuardar(): boolean {
    return (
      !this.loading() &&
      !this.cargaFallida() &&
      !this.saving() &&
      this.form.valid &&
      this.hayCambios()
    );
  }

  /** Lo que dice el pie: en que punto esta la edicion. */
  estadoPie(): { clave: string; icono: IconName; tono: string } {
    if (this.loading())
      return { clave: 'PROFILE.STATUS.LOADING', icono: 'LoaderCircle', tono: 'neutro' };
    if (this.cargaFallida()) {
      return { clave: 'PROFILE.DIALOG.STATUS.NO_DATA', icono: 'CircleAlert', tono: 'error' };
    }
    if (this.form.invalid) {
      return { clave: 'PROFILE.DIALOG.STATUS.INVALID', icono: 'CircleAlert', tono: 'aviso' };
    }
    if (this.hayCambios()) {
      return { clave: 'PROFILE.DIALOG.STATUS.DIRTY', icono: 'Pencil', tono: 'fuerte' };
    }
    return { clave: 'PROFILE.DIALOG.STATUS.NO_CHANGES', icono: 'CircleCheck', tono: 'neutro' };
  }
}
